import { Controller } from '@hotwired/stimulus';
import { closeAllModals, getModalById, getModalByUrl } from '../utilities/modal';

export default class extends Controller {

    connect() {
        super.connect();

        const currentPathWithQueryAndHash = window.location.pathname + window.location.search + window.location.hash;
        const excludedPaths = ['/savitarna', '/prisijungti', '/slaptazodzio-priminimas'];
        const isExcludedPath = excludedPaths.some(path => currentPathWithQueryAndHash.startsWith(path));

        if (!isExcludedPath) {
            sessionStorage.setItem('loginRedirectUrl', currentPathWithQueryAndHash);
        }

        this.headerEl = this.hasHeaderTarget
            ? this.headerTarget
            : document.querySelector(".the-subheader-container");

        if (!this.headerEl) return;

        this.root = document.documentElement;
        this.scheduled = false;

        this.updateMegaTop = () => {
            const rect = this.headerEl.getBoundingClientRect();
            const top = Math.max(0, Math.round(rect.bottom));
            this.root.style.setProperty("--megamenu-top", `${top}px`);
        };

        this.schedule = () => {
            if (this.scheduled) return;
            this.scheduled = true;
            requestAnimationFrame(() => {
                this.updateMegaTop();
                this.scheduled = false;
            });
        };

        window.addEventListener("scroll", this.schedule, { passive: true });
        window.addEventListener("resize", this.schedule);

        if ("ResizeObserver" in window) {
            this.ro = new ResizeObserver(this.schedule);
            this.ro.observe(this.headerEl);
        }

        this.updateMegaTop();
    }

    disconnect() {
        window.removeEventListener("scroll", this.schedule);
        window.removeEventListener("resize", this.schedule);
        if (this.ro) this.ro.disconnect();
    }

    addToCart(e) {
        const button  = e.currentTarget;
        const rawCart = button.dataset.cart;
        if (!rawCart) return;

        let payload;
        try {
            payload = JSON.parse(rawCart);
        } catch (err) {
            console.error('[addToCart] Invalid data-cart JSON:', err);
            return;
        }

        const data  = payload?.data;
        const group = payload?.group;
        if (!data || !group) return;

        if (window.floatingCart) {
            if (window.location.hostname === 'svjc.lt' || window.location.hostname === 'www.svjc.lt') {
                const analyticsId = data.contract_id || data.product_id || data.membership_id;

                if (typeof fbq === 'function') {
                    fbq('track', 'AddToCart', {
                        content_ids:  [analyticsId],
                        content_name: data.title,
                        value:        Number(data.price).toFixed(2),
                        currency:     'EUR',
                        content_type: data.type,
                    });
                }

                if (typeof gtag === 'function') {
                    gtag('event', 'add_to_cart', {
                        items: [{
                            item_id:   analyticsId,
                            item_name: data.title,
                            price:     Number(data.price),
                            category:  data.type,
                            quantity:  1,
                        }],
                        value:    Number(data.price),
                        currency: 'EUR',
                    });
                }
            }

            window.floatingCart.add(data, group);
        }

        this.openRecommendationsModal(data, button);
    }

    async openRecommendationsModal(itemData, triggerButton = null) {
        if (triggerButton) {
            triggerButton.classList.add('loading');
            triggerButton.disabled = true;
        }

        const hasExistingModal = !!document.getElementById('cart-recommendations-modal');

        try {
            const response = await fetch('/wp-json/api/cart/recommendations', {
                method: 'POST',
                headers: {
                    'Content-Type':     'application/json',
                    'Accept':           'text/html',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    item_id:     itemData.contract_id || itemData.product_id || itemData.membership_id,
                    type:        itemData.type,
                    quantity:    1,
                    contract_id: itemData.contract_id || null,
                    group_id:    itemData.group_id    || null,
                    post_id:     itemData.post_id     || null,
                }),
            });

            if (!response.ok) {
                console.warn('[cart-recommendations] Request failed:', response.status);
                return;
            }

            const html = (await response.text()).trim();
            if (!html) return;

            const template = document.createElement('template');
            template.innerHTML = html;
            const newModal = template.content.firstElementChild;
            if (!newModal) return;

            if (hasExistingModal) {
                newModal.classList.add('is-open');
                document.getElementById('cart-recommendations-modal').replaceWith(newModal);
            } else {
                document.body.appendChild(newModal);
            }
        } catch (err) {
            console.error('[cart-recommendations] Failed to open modal:', err);
        } finally {
            if (triggerButton && document.body.contains(triggerButton)) {
                triggerButton.classList.remove('loading');
                triggerButton.disabled = false;
            }
        }
    }

    toggleMobileMenu() {
        const hamburgerIcon = document.querySelector('.hamburger-icon');
        const closeIcon = document.querySelector('.close-icon');
        document.documentElement.classList.toggle('mobile-open');

        hamburgerIcon.classList.toggle('d-none');
        closeIcon.classList.toggle('d-none');
    }

    /**
     * Helper method to submit form on custom event.
     * @param {Event} event
     */
    submitForm(event) {
        event.currentTarget.submit();
    }

    async openModal(event) {
        const element = event.currentTarget;

        if (element.tagName.toLowerCase() === 'button') {
            element.classList.add('is-loading');
        } else if (element.tagName.toLowerCase() === 'a') {
            const originalWidth = element.offsetWidth;
            element.style.width = `${originalWidth}px`;
            element.classList.add('is-loading-link');
        }

        try {
            if (!event.params.id && !event.params.url) {
                throw Error('No [id] or [url] param provided.');
            }

            let modalController = null;

            if (event.params.id) {
                modalController = getModalById(event.params.id);
            } else if (event.params.url) {
                let modalUrl = event.params.url;

                if (event.params.form) {
                    const form = element.closest('form');

                    if (form) {
                        const formData = new FormData(form);
                        const params = new URLSearchParams(formData).toString();

                        if (modalUrl.includes('?')) {
                            modalUrl += '&' + params;
                        } else {
                            modalUrl += '?' + params;
                        }
                    }
                }

                modalController = await getModalByUrl(modalUrl);
            }

            modalController?.open();
        } catch (error) {
            console.error('Error opening modal:', error);
        } finally {
            if (element.tagName.toLowerCase() === 'button') {
                element.classList.remove('is-loading');
            } else if (element.tagName.toLowerCase() === 'a') {
                element.classList.remove('is-loading-link');
                element.style.width = '';
            }
        }
    }

    closeAllModals() {
        closeAllModals();
    }

    reload() {
        window.location.reload();
    }
}
